import { useDroppable } from '@dnd-kit/react'
import { CaretLeftIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/ui/icon'
import { Tooltip } from '@/components/ui/tooltip'

import type { FolderBreadcrumb } from '../community-essentials.types'
import * as S from './breadcrumbs.styled'

interface BreadcrumbsProps {
  folderPath: FolderBreadcrumb[]
  onBack: () => void
  onNavigate: (index: number) => void
}

const DroppableCrumb = ({
  crumb,
  index,
  onNavigate,
}: {
  crumb: FolderBreadcrumb
  index: number
  onNavigate: (index: number) => void
}) => {
  const { ref, isDropTarget } = useDroppable({ id: `drop-breadcrumb-${crumb.id}` })

  return (
    <S.Crumb ref={ref}>
      <Tooltip
        trigger={
          <S.CrumbLink $isDropTarget={isDropTarget} onClick={() => onNavigate(index)}>
            {crumb.name}
          </S.CrumbLink>
        }
      >
        {crumb.name}
      </Tooltip>
      <S.Separator>/</S.Separator>
    </S.Crumb>
  )
}

export const Breadcrumbs = ({ folderPath, onBack, onNavigate }: BreadcrumbsProps) => {
  const { t } = useTranslation(['common'])
  const currentFolder = folderPath[folderPath.length - 1]
  const ancestors = folderPath.slice(1, -1)

  const { ref: rootRef, isDropTarget: isRootDropTarget } = useDroppable({
    id: 'drop-breadcrumb-0',
  })

  return (
    <S.Container>
      <S.BackButton ref={rootRef} $isDropTarget={isRootDropTarget} onClick={onBack}>
        <Icon icon={CaretLeftIcon} size="1.6rem" />
        {t('common:actions.back')}
      </S.BackButton>

      {ancestors.length > 0 && (
        <S.Trail>
          {ancestors.map((crumb, i) => (
            <DroppableCrumb key={crumb.id} crumb={crumb} index={i + 1} onNavigate={onNavigate} />
          ))}
        </S.Trail>
      )}

      <Tooltip trigger={<S.CurrentFolder>{currentFolder.name}</S.CurrentFolder>}>
        {currentFolder.name}
      </Tooltip>
    </S.Container>
  )
}
